// [PURPOSE]: Pure helpers for spending classResources points to cast spells
//   that grant "spend N <resource> to cast" mechanics (Way of Shadow Shadow
//   Arts ki spells, Sorcerer Shadow Magic Eyes of the Dark sorcery-point
//   Darkness, etc.). Mirrors perRestSpellUsage.js for the per-rest spells.
// [WHY]: classResources are stored at playerStats.classes[i][resourceId] =
//   { current, max }. Spell cast logic needs to decrement on cast and read
//   availability before allowing the cast. Centralizing here prevents drift
//   between consumers (spellCastingService, SpellbookTab, future reset hooks).
// [INTEGRATION]: spellCastingService decrements on cast, SpellbookTab reads
//   for the cast-button enable, RestPanel doesn't need a hook here because
//   the resources themselves recharge through the existing classResources
//   recharge cycle (rechargesOnRest in classResources.js).
// [Phase 4b BC-13 closeout — APR 2026]: Closes the resource-cost spell gap.

/**
 * Find the class entry index that owns the given classResources resource id
 * (e.g., 'ki_points', 'sorcery_points'). Resources live on
 * playerStats.classes[i][resourceId] when minLevel is met. Returns -1 when
 * no class owns it.
 *
 * @param {Object} playerStats
 * @param {string} resourceId
 * @returns {number} class index in playerStats.classes (or -1)
 */
export function findResourceClassIndex(playerStats, resourceId) {
  if (!playerStats || !resourceId) return -1;
  const classes = playerStats.classes || [];
  for (let i = 0; i < classes.length; i++) {
    if (classes[i] && classes[i][resourceId] != null) return i;
  }
  return -1;
}

/**
 * Read current/max for a resource. Returns { current: 0, max: 0 } if the
 * resource isn't on any class entry.
 *
 * @param {Object} playerStats
 * @param {string} resourceId
 * @returns {{current: number, max: number}}
 */
export function readResourceState(playerStats, resourceId) {
  const idx = findResourceClassIndex(playerStats, resourceId);
  if (idx < 0) return { current: 0, max: 0 };
  const stored = playerStats.classes[idx][resourceId] || {};
  return {
    current: Number(stored.current) || 0,
    max: Number(stored.max) || 0,
  };
}

/**
 * Decrement a resource by `cost`. Clamps at 0 (no negative balance). Returns
 * the same playerStats reference if the cost couldn't be paid (resource not
 * found OR current < cost) — caller should pre-check via hasResourceAvailable.
 *
 * @param {Object} playerStats
 * @param {string} resourceId
 * @param {number} cost
 * @returns {Object} new playerStats (or same if no-op)
 */
export function spendResource(playerStats, resourceId, cost) {
  if (!playerStats || !resourceId || typeof cost !== 'number' || cost <= 0) {
    return playerStats;
  }
  const idx = findResourceClassIndex(playerStats, resourceId);
  if (idx < 0) return playerStats;
  const classEntry = playerStats.classes[idx];
  const stored = classEntry[resourceId] || {};
  const current = Number(stored.current) || 0;
  if (current < cost) return playerStats; // no-op when can't afford

  const nextClasses = playerStats.classes.map((c, i) => {
    if (i !== idx) return c;
    return {
      ...c,
      [resourceId]: {
        ...stored,
        current: Math.max(0, current - cost),
      },
    };
  });
  return { ...playerStats, classes: nextClasses };
}

/**
 * Predicate: does the character currently have at least `cost` of this
 * resource available?
 */
export function hasResourceAvailable(playerStats, resourceId, cost) {
  if (typeof cost !== 'number' || cost <= 0) return false;
  const { current } = readResourceState(playerStats, resourceId);
  return current >= cost;
}
